import { useMedia, useMount } from 'react-use';
import { useCallback, useEffect } from 'react';
import cn from 'clsx';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { Icons } from '../Icons';
import styles from './NightSwitch.module.scss';

const DARK_THEME_KEY = 'dark-theme';

interface NightSwitchProps {
  className?: string;
}

const EVENT = 'DARK_THEME_SWITCHED';
type EventPayload = {
  isDark: boolean;
};

const setTheme = (isDark: boolean) => {
  if (isDark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  document.dispatchEvent(
    new CustomEvent(EVENT, { detail: { isDark } satisfies EventPayload })
  );
};

export const subscribeToThemeChange = (cb: (isDark: boolean) => void) => {
  const signal = new AbortController();

  document.addEventListener(
    EVENT,
    (e) => {
      const { detail } = e as CustomEvent<EventPayload>;
      cb(detail.isDark);
    },
    signal
  );
  return () => signal.abort();
};

export function useIsDark() {
  const isDefaultNight = useMedia('(prefers-color-scheme: dark)');
  const [isDark, setIsDark] = useLocalStorage(DARK_THEME_KEY, isDefaultNight);

  useEffect(() => {
    setTheme(!!isDark);
    return subscribeToThemeChange((v) => {
      if (v === isDark) return;
      setIsDark(v);
    });
  }, [isDark, setIsDark]);

  const toggle = useCallback(() => {
    setIsDark((v) => !v);
  }, [setIsDark]);

  return [isDark, toggle] as const;
}

export function NightSwitch({ className }: NightSwitchProps) {
  const [isDark, toggle] = useIsDark();

  const handleClick = useCallback(() => {
    toggle();
  }, [toggle]);

  return (
    <div
      className={cn(className, styles.toggler)}
      onClick={handleClick}
      role="none"
    >
      {isDark ? <Icons.Day /> : <Icons.Night />}
    </div>
  );
}
